// The locked 11-question EA onboarding questionnaire — the hard gate.
//
// `docs/DECISION_LOG.md` (`DONNAP-REVIEW-AND-EA-ONBOARDING-LOCKED`): the EA
// does not go live/usable for a user until `onboarding_complete` is true, and
// there is no skip option. This component IS that gate on the frontend; the
// real enforcement is server-side (`services/ea/lifecycle.py:activate`
// refuses before `onboarding_complete`), so a user who somehow bypassed this
// screen would still be refused activation by the backend.
//
// SINGLE SCROLLABLE PAGE, NOT A STEP WIZARD
// ==========================================
// The codebase has one precedent for a multi-step form
// (`components/CreativeEngine.jsx`'s `StepIndicator` — a 7-stage production
// pipeline where each stage calls its own endpoint and persists
// independently). That shape fits a workflow with real per-stage state; it
// does not fit eleven short questions answered once and submitted together.
// Eleven "Next" clicks would fight the framing copy's own promise ("about 3
// minutes") more than one scrollable page with a live progress count and a
// single submit — so that is what this is. If this ever grows past ~15
// questions, a stepper is the right call at that point.
//
// STRUCTURED INPUTS ARE REAL CONTROLS
// ====================================
// Working hours uses `<input type="time">` for start/end, a real day-of-week
// multi-select (toggle buttons, not a text field), and a timezone `<select>`
// seeded from `Intl.supportedValuesOf("timeZone")` and defaulted to the
// browser's own detected zone. Notification preferences are two
// `single-select` groups. Neither asks the operator to type a time range or
// a channel name.
import React, { useEffect, useMemo, useState } from "react";

import { Block, Button } from "../settings/ui";
import { get, post, ENDPOINTS } from "./api";
import {
  CLOSING_COPY,
  DAYS_OF_WEEK,
  FRAMING_COPY,
  NO_RECURRING_RESPONSIBILITIES,
  QUESTIONS,
  detectTimezone,
  listTimezones,
} from "./questions";

const OTHER = "Something else";

/** Raw per-question editor state -> the value the backend actually stores.
 * `null` means "not yet answered" and is what drives progress + the submit
 * gate. */
function resolvedValue(q, raw) {
  switch (q.type) {
    case "single-select": {
      if (q.allowOther) {
        const r = raw || {};
        if (r.choice === OTHER) return (r.other || "").trim() || null;
        return r.choice || null;
      }
      return raw || null;
    }
    case "working-hours": {
      const r = raw || {};
      if (!r.start || !r.end || !r.timezone || !(r.days || []).length) return null;
      return { start: r.start, end: r.end, days: r.days, timezone: r.timezone };
    }
    case "notification-preferences": {
      const r = raw || {};
      if (!r.channel || !r.frequency) return null;
      return { channel: r.channel, frequency: r.frequency };
    }
    case "open-text-list": {
      const list = (raw || []).map((s) => (s || "").trim()).filter(Boolean);
      return list.length ? list : null;
    }
    case "open-text": {
      const s = (raw || "").trim();
      if (s) return s;
      return q.optional ? NO_RECURRING_RESPONSIBILITIES : null;
    }
    default:
      return null;
  }
}

/** The inverse — an existing stored value (from a partial or prior
 * submission) back into the editor's raw shape, so re-opening the
 * questionnaire prefills rather than blanking what was already answered. */
function rawFromStored(q, stored) {
  if (stored === null || stored === undefined) return undefined;
  switch (q.type) {
    case "single-select": {
      if (q.allowOther) {
        if (typeof stored !== "string" || !stored) return undefined;
        const known = q.options.filter((o) => o !== OTHER).includes(stored);
        return known ? { choice: stored, other: "" } : { choice: OTHER, other: stored };
      }
      return typeof stored === "string" ? stored : undefined;
    }
    case "working-hours":
      return typeof stored === "object" && stored
        ? { start: stored.start || "", end: stored.end || "", days: stored.days || [],
            timezone: stored.timezone || detectTimezone() }
        : undefined;
    case "notification-preferences":
      return typeof stored === "object" && stored
        ? { channel: stored.channel || "", frequency: stored.frequency || "" }
        : undefined;
    case "open-text-list":
      return Array.isArray(stored) ? stored.slice(0, q.max) : undefined;
    case "open-text":
      if (typeof stored !== "string") return undefined;
      return stored === NO_RECURRING_RESPONSIBILITIES ? "" : stored;
    default:
      return undefined;
  }
}

function SingleSelect({ question, raw, onChange }) {
  const r = question.allowOther ? raw || {} : null;
  const selected = question.allowOther ? r.choice : raw;

  return (
    <div className="eaob-options" role="radiogroup" aria-label={question.prompt}>
      {question.options.map((opt) => (
        <button
          key={opt}
          type="button"
          role="radio"
          aria-checked={selected === opt}
          className={`eaob-option${selected === opt ? " eaob-option-selected" : ""}`}
          onClick={() =>
            onChange(
              question.allowOther ? { choice: opt, other: r.other || "" } : opt
            )
          }
        >
          {opt}
        </button>
      ))}
      {question.allowOther && r.choice === OTHER ? (
        <input
          type="text"
          className="set-input eaob-other-input"
          placeholder="Tell us more"
          value={r.other || ""}
          onChange={(e) => onChange({ choice: OTHER, other: e.target.value })}
          autoFocus
        />
      ) : null}
    </div>
  );
}

function WorkingHours({ raw, onChange }) {
  const r = raw || { start: "", end: "", days: [], timezone: detectTimezone() };
  const zones = useMemo(() => listTimezones(), []);

  function toggleDay(id) {
    const days = r.days.includes(id) ? r.days.filter((d) => d !== id) : [...r.days, id];
    onChange({ ...r, days });
  }

  return (
    <div className="eaob-hours">
      <div className="eaob-hours-row">
        <label className="eaob-field">
          <span className="eaob-field-label dim">Start</span>
          <input
            type="time"
            className="set-input"
            value={r.start}
            onChange={(e) => onChange({ ...r, start: e.target.value })}
          />
        </label>
        <label className="eaob-field">
          <span className="eaob-field-label dim">End</span>
          <input
            type="time"
            className="set-input"
            value={r.end}
            onChange={(e) => onChange({ ...r, end: e.target.value })}
          />
        </label>
        <label className="eaob-field">
          <span className="eaob-field-label dim">Timezone</span>
          <select
            className="set-input"
            value={r.timezone}
            onChange={(e) => onChange({ ...r, timezone: e.target.value })}
          >
            {!zones.includes(r.timezone) && r.timezone ? (
              <option value={r.timezone}>{r.timezone}</option>
            ) : null}
            {zones.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="eaob-days" role="group" aria-label="Working days">
        {DAYS_OF_WEEK.map((d) => (
          <button
            key={d.id}
            type="button"
            aria-pressed={r.days.includes(d.id)}
            className={`eaob-day${r.days.includes(d.id) ? " eaob-day-selected" : ""}`}
            onClick={() => toggleDay(d.id)}
          >
            {d.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function NotificationPreferences({ question, raw, onChange }) {
  const r = raw || { channel: "", frequency: "" };
  return (
    <div className="eaob-notif">
      <div>
        <div className="eaob-subprompt dim">Channel</div>
        <div className="eaob-options" role="radiogroup" aria-label="Notification channel">
          {question.channelOptions.map((opt) => (
            <button
              key={opt}
              type="button"
              role="radio"
              aria-checked={r.channel === opt}
              className={`eaob-option${r.channel === opt ? " eaob-option-selected" : ""}`}
              onClick={() => onChange({ ...r, channel: opt })}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="eaob-subprompt dim">Frequency</div>
        <div className="eaob-options" role="radiogroup" aria-label="Notification frequency">
          {question.frequencyOptions.map((opt) => (
            <button
              key={opt}
              type="button"
              role="radio"
              aria-checked={r.frequency === opt}
              className={`eaob-option${r.frequency === opt ? " eaob-option-selected" : ""}`}
              onClick={() => onChange({ ...r, frequency: opt })}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function PriorityList({ question, raw, onChange }) {
  const r = raw && raw.length ? raw : ["", "", ""];
  return (
    <div className="eaob-priorities">
      {Array.from({ length: question.max }, (_, i) => (
        <input
          key={i}
          type="text"
          className="set-input eaob-priority-input"
          placeholder={i === 0 ? question.placeholder : `Priority ${i + 1} (optional)`}
          value={r[i] || ""}
          onChange={(e) => {
            const next = [...r];
            next[i] = e.target.value;
            onChange(next);
          }}
        />
      ))}
    </div>
  );
}

function Question({ question, raw, onChange }) {
  return (
    <div className="eaob-question" data-answered={resolvedValue(question, raw) != null}>
      <div className="eaob-question-head">
        <span className="eaob-question-n">{question.n}</span>
        <div>
          <div className="eaob-question-label">{question.label}</div>
          <div className="eaob-question-prompt dim">{question.prompt}</div>
        </div>
      </div>
      <div className="eaob-question-body">
        {question.type === "single-select" ? (
          <SingleSelect question={question} raw={raw} onChange={onChange} />
        ) : null}
        {question.type === "working-hours" ? (
          <WorkingHours raw={raw} onChange={onChange} />
        ) : null}
        {question.type === "notification-preferences" ? (
          <NotificationPreferences question={question} raw={raw} onChange={onChange} />
        ) : null}
        {question.type === "open-text-list" ? (
          <PriorityList question={question} raw={raw} onChange={onChange} />
        ) : null}
        {question.type === "open-text" ? (
          <textarea
            className="set-input eaob-textarea"
            placeholder={question.placeholder}
            value={raw || ""}
            onChange={(e) => onChange(e.target.value)}
            rows={2}
          />
        ) : null}
      </div>
    </div>
  );
}

/**
 * `onComplete` (optional): called once the server confirms
 * `onboarding_complete: true`. `ea.jsx` uses it to stop gating the area.
 */
export default function EAOnboarding({ onComplete }) {
  const [load, setLoad] = useState({ status: "loading" });
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const res = await get(ENDPOINTS.myEA);
      if (!alive) return;
      setLoad(res);
      if (res.status === "ok") {
        const stored = (res.data && res.data.ea && res.data.ea.onboarding) || {};
        const prefill = {};
        for (const q of QUESTIONS) {
          const r = rawFromStored(q, stored[q.field]);
          if (r !== undefined) prefill[q.field] = r;
        }
        setAnswers(prefill);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const values = useMemo(() => {
    const out = {};
    for (const q of QUESTIONS) out[q.field] = resolvedValue(q, answers[q.field]);
    return out;
  }, [answers]);

  const answeredCount = QUESTIONS.filter((q) => values[q.field] != null).length;
  const canSubmit = answeredCount === QUESTIONS.length && !submitting;

  function setField(field, raw) {
    setAnswers((a) => ({ ...a, [field]: raw }));
  }

  async function submit() {
    setAttempted(true);
    if (answeredCount !== QUESTIONS.length) return;
    setSubmitting(true);
    setSubmitError("");
    const payload = {};
    for (const q of QUESTIONS) payload[q.field] = values[q.field];
    const res = await post(ENDPOINTS.onboarding, payload);
    setSubmitting(false);
    if (res.status !== "ok") {
      setSubmitError(res.error || "Could not save your answers. Nothing was lost — try again.");
      return;
    }
    if (res.data && res.data.onboarding_complete) {
      setDone(true);
      if (onComplete) onComplete(res.data);
    } else {
      // All eleven were sent; a false here means the server disagrees about
      // what counts as answered. Say so plainly rather than looping silently.
      setSubmitError(
        `Saved, but the server still lists these as missing: ${(res.data?.missing || []).join(", ")}.`
      );
    }
  }

  if (load.status === "loading") {
    return (
      <Block title="Executive Assistant setup">
        <div className="eaob-loading dim">Loading…</div>
      </Block>
    );
  }

  if (load.status === "disabled") {
    return (
      <Block
        title="Executive Assistant setup"
        note="This surface is not enabled in this environment."
      >
        <p className="dim">
          The Personal EA runtime (<code className="mono">CIPHER_PERSONAL_EA_V1</code>) is off.
          This questionnaire is fully built and will appear here the moment it is turned on —
          nothing further needs to change when it is.
        </p>
      </Block>
    );
  }

  if (load.status === "denied") {
    return (
      <Block title="Executive Assistant setup">
        <p className="dim">The server refused this read. Sign in and try again.</p>
      </Block>
    );
  }

  if (load.status === "error") {
    return (
      <Block title="Executive Assistant setup">
        <p className="dim" role="alert">
          Could not load your assistant. {load.error || ""}
        </p>
      </Block>
    );
  }

  if (done) {
    return (
      <Block title="You're all set" note="Onboarding complete.">
        <p>{CLOSING_COPY}</p>
      </Block>
    );
  }

  return (
    <Block
      title="Set up your Executive Assistant"
      note={FRAMING_COPY}
      actions={
        <span className="eaob-progress dim">
          {answeredCount} of {QUESTIONS.length} answered
        </span>
      }
    >
      <div className="eaob-questions">
        {QUESTIONS.map((q) => (
          <Question
            key={q.field}
            question={q}
            raw={answers[q.field]}
            onChange={(raw) => setField(q.field, raw)}
          />
        ))}
      </div>

      {attempted && !canSubmit ? (
        <p className="eaob-note" role="alert">
          A few questions still need an answer before this can be submitted —
          {" "}
          {QUESTIONS.filter((q) => values[q.field] == null).map((q) => q.label).join(", ")}.
        </p>
      ) : null}
      {submitError ? (
        <p className="eaob-note" role="alert">
          {submitError}
        </p>
      ) : null}

      <div className="eaob-submit-row">
        <Button onClick={submit} disabled={submitting}>
          {submitting ? "Saving…" : "Finish setup"}
        </Button>
      </div>
    </Block>
  );
}
